import { generateAuthorizePayload } from "@ekidenfi/ts-sdk";

import { getEkidenSdkClient } from "@src/api/sdk";

import type { Wallet } from "../account/types";
import { storageService } from "../storage";

export class AuthService {
  isTokenExpired = (token: string): boolean => {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) {
        return true;
      }

      const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));

      if (payload.exp) {
        const nowSeconds = Math.floor(Date.now() / 1000);
        return nowSeconds >= payload.exp - 60;
      }

      return true;
    } catch {
      return true;
    }
  };

  generateRootAccountToken = async (wallet: Wallet): Promise<string | null> => {
    if (!wallet.account) {
      return null;
    }

    const { timestamp_ms, nonce, message, full_message } = generateAuthorizePayload();

    const signature = await wallet.signMessage({
      message,
      nonce,
      address: false,
      application: false,
      chainId: false,
    });

    const sig = signature.signature as any;
    let signatureHex: string;
    if (typeof sig === "string") {
      signatureHex = sig.startsWith("0x") ? sig : `0x${sig}`;
    } else if (sig?.toString && typeof sig.toString === "function") {
      signatureHex = sig.toString();
    } else {
      signatureHex = String(sig);
    }

    const publicKeyBytes = wallet.account.publicKey?.toUint8Array() as Uint8Array | undefined;
    const publicKeyHex = publicKeyBytes
      ? `0x${Array.from(publicKeyBytes)
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("")}`
      : "";

    const data = await getEkidenSdkClient().user.authorize({
      signature: signatureHex,
      public_key: publicKeyHex,
      timestamp_ms,
      nonce,
      full_message,
    });

    await storageService.setRootToken(data.token);
    await storageService.setRootTokenLastRequested(Date.now());

    return data.token;
  };
}

export const authService = new AuthService();
